import { FlexRowBetween, PageHeader, PlanUsageStrip } from 'components'
import { UserList } from './list'
import { CustomerAssignModal } from './CustomerAssignModal'
import { TemplateAssignModal } from './TemplateAssignModal'
import { UserEditModal } from './UserEditModal'
import { useState, useMemo } from 'react'
import { Confirm } from 'semantic-ui-react'
import { useAuth } from 'context'
import { useSubscriptionUsage } from 'context/subscription-usage'
import { GetTeamMemberList } from 'hooks'
import { UsersService } from 'utility/services/user'
import { useNotification } from 'hooks/toast'
import { userUpdateErrorMessage } from 'utility/user-deactivate'

export const UserScreen = () => {
    const [selectedUser, setSelectedUser] = useState(null)
    const [showCustomerModal, setShowCustomerModal] = useState(false)
    const [showTemplateModal, setShowTemplateModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [deactivateTarget, setDeactivateTarget] = useState(null)
    const { user } = useAuth()
    const { errorToast, successToast } = useNotification()
    const { isFreePlan } = useSubscriptionUsage()
    const { data, loading, filter, pageChanged, totalCount: apiTotalCount, refresh } = GetTeamMemberList()

    // Filter out the current logged-in user
    const teamMembers = useMemo(() => {
        return (data || []).filter(member => member.Email !== user?.Email)
    }, [data, user?.Email])

    // Adjust totalCount to account for filtered user
    // If API returns paginated response, use that count minus 1 (current user)
    // Otherwise, use the filtered array length
    const adjustedTotalCount = useMemo(() => {
        if (apiTotalCount > 0 && Array.isArray(data) && data.length === apiTotalCount) {
            // Backend supports pagination - subtract 1 for current user if they're in the list
            const currentUserInList = data.some(member => member.Email === user?.Email)
            return currentUserInList ? apiTotalCount - 1 : apiTotalCount
        }
        // Backend doesn't support pagination yet - use filtered array length
        return teamMembers.length
    }, [apiTotalCount, data, teamMembers.length, user?.Email])

    const handleAssignCustomer = user => {
        setSelectedUser(user)
        setShowCustomerModal(true)
    }

    const handleAssignTemplate = user => {
        setSelectedUser(user)
        setShowTemplateModal(true)
    }

    // Asks first: once inactive the user drops out of this list (no way back from here yet).
    const handleDeactivate = member => setDeactivateTarget(member)

    const confirmDeactivate = async () => {
        const member = deactivateTarget
        setDeactivateTarget(null)
        if (!member) return
        try {
            await UsersService.deactivateMember(member)
            successToast('User deactivated')
            refresh()
        } catch (err) {
            errorToast(userUpdateErrorMessage(err, 'Could not deactivate this user'))
        }
    }

    const handleEditUser = user => {
        setSelectedUser(user)
        setShowEditModal(true)
    }

    const handleCustomerModalClose = shouldRefresh => {
        setShowCustomerModal(false)
        setSelectedUser(null)
        if (shouldRefresh) {
            refresh()
        }
    }

    const handleTemplateModalClose = shouldRefresh => {
        setShowTemplateModal(false)
        setSelectedUser(null)
        if (shouldRefresh) {
            refresh()
        }
    }

    const handleEditModalClose = shouldRefresh => {
        setShowEditModal(false)
        setSelectedUser(null)
        if (shouldRefresh) {
            refresh()
        }
    }

    return (
        <>
            <FlexRowBetween>
                <PageHeader header="User List" />
            </FlexRowBetween>
            {isFreePlan && <PlanUsageStrip metric="seats" />}
            <div className="my-3">
                <UserList
                    data={teamMembers}
                    loading={loading}
                    filter={filter}
                    pageChanged={pageChanged}
                    totalCount={adjustedTotalCount}
                    onAssignCustomer={handleAssignCustomer}
                    onAssignTemplate={handleAssignTemplate}
                    onEditUser={handleEditUser}
                    onDeactivate={handleDeactivate}
                />
            </div>
            <CustomerAssignModal
                open={showCustomerModal}
                onClose={handleCustomerModalClose}
                userId={selectedUser?.unique_id}
            />
            <TemplateAssignModal
                open={showTemplateModal}
                onClose={handleTemplateModalClose}
                userId={selectedUser?.unique_id}
            />
            <UserEditModal
                open={showEditModal}
                onClose={handleEditModalClose}
                user={selectedUser}
            />
            <Confirm
                open={!!deactivateTarget}
                header="Deactivate user"
                content={`Deactivate ${deactivateTarget?.Name || deactivateTarget?.Email || 'this user'}? They can no longer sign in and will no longer appear in this list.`}
                confirmButton="Deactivate"
                onCancel={() => setDeactivateTarget(null)}
                onConfirm={confirmDeactivate}
            />
        </>
    )
}
